<script lang="ts">
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import ClickableTable from "/@lib/components/table/ClickableTable.svelte"
  import { TABLE_STORAGE_KEYS } from "/@lib/components/table/column-layout"
  import { twApiClient } from "/@lib/tastyworks-rest"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import { formatReceivedTime } from "/@lib/utils/formats"
  import { pendingCashEntriesByAccountNumberUrl } from "/@lib/utils/urls"
  import {
    CustomerAccountRowResult,
    ItemsResponse,
    UrlPathBuilder,
    pendingCashEntry,
  } from "@tastyworks/tastyworks-api"
  import { Button, Dialog } from "@tastyworks/ui-library"

  const PENDING_CASH_COLUMNS: ColumnDefinitions = [
    {
      name: "Account #",
      dataFormatter: (entry: pendingCashEntry) => entry.accountNumber,
      width: 3,
    },
    {
      name: "Amount",
      dataFormatter: (entry: pendingCashEntry) => entry.amount,
      width: 2,
      comparator: (entry: pendingCashEntry) => Number(entry.amount),
    },
    {
      name: "Apex Cash Activity",
      dataFormatter: (entry: pendingCashEntry) => entry.apexCashActivityId,
      linkFormatter: (entry: pendingCashEntry) =>
        new UrlPathBuilder(import.meta.env.VITE_TW_BASE_URL)
          .nested("admin")
          .nested("apex_cash_movements")
          .toUrl(entry.apexCashActivityId),
      width: 4,
    },
    {
      name: "Description",
      dataFormatter: (entry: pendingCashEntry) => entry.description,
      width: 14,
    },
    {
      name: "Effect",
      dataFormatter: (entry: pendingCashEntry) => entry.effect,
      width: 2,
    },
    {
      name: "Expiration Date",
      dataFormatter: (entry: pendingCashEntry) => entry.expirationDate,
      comparator: (entry: pendingCashEntry) => new Date(entry.expirationDate),
      width: 4,
    },
    {
      name: "Id",
      dataFormatter: (entry: pendingCashEntry) => entry.id,
      linkFormatter: (entry: pendingCashEntry) =>
        new UrlPathBuilder(import.meta.env.VITE_TW_BASE_URL)
          .nested("admin")
          .nested("pending_cash_entries")
          .toUrl(entry.id),
      width: 3,
    },
    {
      name: "Released At",
      dataFormatter: (entry: pendingCashEntry) =>
        formatReceivedTime(entry.releasedAt),
      comparator: (entry: pendingCashEntry) => new Date(entry.releasedAt),
      width: 5,
    },
    {
      name: "Released By User",
      dataFormatter: (entry: pendingCashEntry) => entry.releasedByUserId,
      linkFormatter: (entry: pendingCashEntry) =>
        new UrlPathBuilder(import.meta.env.VITE_TW_BASE_URL)
          .nested("admin")
          .nested("users")
          .toUrl(entry.releasedByUserId),
      comparator: (entry: pendingCashEntry) => Number(entry.releasedByUserId),
      width: 6,
    },
  ]

  let pendingCashPromise = $state<
    Promise<ItemsResponse<pendingCashEntry> | null>
  >(Promise.resolve(null))

  const viewAllUrl = $derived(
    pendingCashEntriesByAccountNumberUrl(
      $selectedCustomerAccount.account?.accountNumber
    )
  )

  function getPendingCashEntries(account: CustomerAccountRowResult) {
    if ($selectedCustomerAccount.account) {
      return twApiClient.AdminBalancesService.pendingCash(
        account.account?.accountNumber ?? ""
      )
    }
    return Promise.reject(new Error("No account number found"))
  }
</script>

<Dialog.Root>
  <button
    class="w-30"
    onclick={() =>
      (pendingCashPromise = getPendingCashEntries($selectedCustomerAccount))}
  >
    <Dialog.Trigger
      disabled={$selectedCustomerAccount.account?.accountNumber == null}
      variant="defaultOutline"
      class="w-30"
      data-testid="trigger"
    >
      View Pending Cash
    </Dialog.Trigger>
  </button>

  <Dialog.Portal>
    <Dialog.Overlay />
    <Dialog.Content
      class="flex h-full w-full min-w-[90vw] flex-col bg-trade-desk-gray-light"
    >
      <div class="flex flex-row items-center justify-between">
        <div class="flex items-center gap-3">
          <span> Pending Cash Entries </span>
          {#if viewAllUrl}
            <Button
              variant="defaultOutline"
              href={viewAllUrl}
              rel="noopener noreferrer"
              data-testid="view-all-link"
            >
              View All
            </Button>
          {/if}
        </div>
        <Dialog.Close />
      </div>
      <div class="relative flex h-0 flex-grow">
        <AwaitItemsResponse
          promise={pendingCashPromise}
          defaultMessage="Pending Cash Entries"
          noResultsMessage="No Entries Found"
          let:items
        >
          <ClickableTable
            storageKey={TABLE_STORAGE_KEYS.pendingCash}
            {items}
            columns={PENDING_CASH_COLUMNS}
          />
        </AwaitItemsResponse>
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
